// ============================================
// components/gunny/GunnyRowDetail.tsx
// Right-side drawer showing the full state of one gunny_utilization row —
// supplier claim, DM-certified, MP-approved blocks side-by-side; condition
// from all three perspectives; timeline of actor stamps; payment history.
// Re-used by Society, Supplier, MP, CAO, and admin views.
// ============================================
import React, { useEffect, useState } from 'react';
import { X, Check, CircleDashed, History, RotateCcw, Loader2, MapPin, Edit3 } from 'lucide-react';
import type { GunnyProformaRow, GunnyAuditEntry } from '../../api/services';
import { gunnyUtilizationPaymentsAPI, gunnyUtilizationAPI, seasonsAPI, godownsAPI } from '../../api/services';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/markfed';

interface Payment {
  id: number;
  paid_on: string;
  amount_rs: number | string;
  mode: string;
  utr_no: string | null;
  bank_name: string | null;
  remarks: string | null;
}

interface Props {
  row: GunnyProformaRow | null;
  onClose: () => void;
  /** Parent can use this to refresh its list after we change the row's status */
  onRowUpdated?: () => void;
}

const moneyFmt = (v: number | string | null | undefined) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const dateFmt = (v: string | null | undefined): string => {
  if (!v) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  try {
    const d = new Date(v);
    if (isNaN(d.valueOf())) return v;
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return v; }
};

export const GunnyRowDetail: React.FC<Props> = ({ row, onClose, onRowUpdated }) => {
  const { hasRole } = useAuth();
  const canReopen = hasRole(UserRole.DM, UserRole.MANAGER_PROCUREMENT, UserRole.AO_CAO);
  const canEditReturnGodown = hasRole(UserRole.DM, UserRole.MANAGER_PROCUREMENT, UserRole.AO_CAO);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [audit, setAudit] = useState<GunnyAuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenErr, setReopenErr] = useState<string | null>(null);
  const [godownOpts, setGodownOpts] = useState<{ id: number; godown_name: string; godown_type: string | null }[]>([]);
  const [returnEditing, setReturnEditing] = useState(false);
  const [returnErr, setReturnErr] = useState<string | null>(null);

  const handleReopen = async () => {
    if (!row) return;
    const reason = window.prompt(
      `Reopen this rejected row?\n\nIt will move back to "draft" so the pipeline can resume.\nOptionally describe why (shown in audit trail):`,
      '',
    );
    if (reason === null) return; // user cancelled
    setReopening(true);
    setReopenErr(null);
    try {
      const season = await seasonsAPI.active();
      const seasonId = season?.data?.id;
      if (!seasonId) throw new Error('No active season configured');
      await gunnyUtilizationAPI.reopen(seasonId, row.id, reason.trim() || undefined);
      if (onRowUpdated) onRowUpdated();
      onClose();
    } catch (e: any) {
      setReopenErr(e?.response?.data?.message || 'Reopen failed');
    } finally {
      setReopening(false);
    }
  };

  useEffect(() => {
    if (!row) return;
    let alive = true;
    setLoadingPayments(true);
    gunnyUtilizationPaymentsAPI
      .list(row.id)
      .then((res) => { if (alive) setPayments(res.data || []); })
      .catch(() => { if (alive) setPayments([]); })
      .finally(() => { if (alive) setLoadingPayments(false); });

    setLoadingAudit(true);
    setAudit([]);
    (async () => {
      try {
        const season = await seasonsAPI.active();
        const seasonId = season?.data?.id;
        if (!seasonId) { if (alive) setLoadingAudit(false); return; }
        const res = await gunnyUtilizationAPI.audit(seasonId, row.id);
        if (alive) setAudit(res.data || []);
      } catch {
        if (alive) setAudit([]);
      } finally {
        if (alive) setLoadingAudit(false);
      }
    })();
    // Godown options for the return-godown picker — scoped to the row's district
    if (canEditReturnGodown) {
      godownsAPI.list(row.district_id).then((res) => {
        if (alive) setGodownOpts((res.data as any[]).filter((g) => g.is_active));
      }).catch(() => {});
    }
    return () => { alive = false; };
  }, [row, canEditReturnGodown]);

  const saveReturnGodown = async (godownId: number | null) => {
    if (!row) return;
    setReturnErr(null);
    try {
      const season = await seasonsAPI.active();
      const seasonId = season?.data?.id;
      if (!seasonId) throw new Error('No active season');
      await gunnyUtilizationAPI.returnGodown(seasonId, row.id, godownId);
      setReturnEditing(false);
      if (onRowUpdated) onRowUpdated();
      onClose();
    } catch (e: any) {
      setReturnErr(e?.response?.data?.message || 'Failed to set return godown');
    }
  };

  if (!row) return null;

  const timeline = row.timeline;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between z-10">
          <div>
            <h2 className="font-semibold text-gray-900">
              Gunny utilization #{row.id}
            </h2>
            <p className="text-xs text-gray-500">
              {row.district} · {row.merchant} · {row.society_name || '—'}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Status: <span className="font-medium">{row.status.replace('_', ' ')}</span>
            </p>
            {reopenErr && (
              <p className="text-xs text-red-600 mt-1">{reopenErr}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {row.status === 'rejected' && canReopen && (
              <button
                onClick={handleReopen}
                disabled={reopening}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
                title="Move this row back to draft so the pipeline can resume"
              >
                {reopening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Reopen
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Timeline */}
          <section>
            <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">Workflow</h3>
            <ol className="space-y-1.5">
              <TimelineItem label="Society confirmed"   at={timeline.society_done_at} />
              <TimelineItem label="Supplier filed claim" at={timeline.supplier_claim_at} />
              <TimelineItem label="DM certified"         at={timeline.dm_verified_at} />
              <TimelineItem label="Mgr Proc approved"    at={timeline.mp_approved_at} />
              <TimelineItem label="Latest payment"       at={timeline.last_paid_on} />
            </ol>
          </section>

          {/* Utilization block (I–O) */}
          <section>
            <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">Society Utilization</h3>
            <Grid>
              <Cell label="Supplied"  v={row.bags_supplied.toLocaleString()} />
              <Cell label="Utilized"  v={row.bags_utilized.toLocaleString()} />
              <Cell label="Returned"  v={row.bags_returned.toLocaleString()} />
              <Cell label="Debit bags" v={row.debit_note_bags.toLocaleString()} />
            </Grid>
            {row.bags_returned > 0 && (
              <div className="mt-3 p-3 border border-gray-200 rounded bg-gray-50 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase text-gray-500 mb-1">
                      <MapPin className="w-3.5 h-3.5" /> Returns landed at
                    </div>
                    {!returnEditing ? (
                      <div className="text-gray-800">
                        <ReturnGodownLabel row={row} godowns={godownOpts} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select
                          defaultValue={row.return_godown_id ?? 0}
                          onChange={(e) => saveReturnGodown(Number(e.target.value) || null)}
                          className="flex-1 px-2 py-1 text-sm border rounded bg-white"
                        >
                          <option value={0}>
                            Source godown (default)
                          </option>
                          {godownOpts.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.godown_name}{g.godown_type ? ` (${g.godown_type.toUpperCase()})` : ''}
                            </option>
                          ))}
                        </select>
                        <button onClick={() => setReturnEditing(false)} className="text-xs text-gray-500 hover:text-gray-700">
                          Cancel
                        </button>
                      </div>
                    )}
                    {returnErr && <p className="text-xs text-red-600 mt-1">{returnErr}</p>}
                  </div>
                  {!returnEditing && canEditReturnGodown && (
                    <button
                      onClick={() => setReturnEditing(true)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100"
                    >
                      <Edit3 className="w-3 h-3" /> Change
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  By default, returned bags credit the source godown. Change this if the bags physically went to a different godown.
                </p>
              </div>
            )}
          </section>

          {/* Condition (AF–AH) */}
          <section>
            <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">Condition Assessments</h3>
            <Grid cols={3}>
              <Cell label="As per Society"   v={row.condition_society || '—'} />
              <Cell label="As per Warehouse" v={row.condition_warehouse || '—'} />
              <Cell label="As per DM"        v={row.condition_dm || '—'} />
            </Grid>
          </section>

          {/* Three bill blocks side-by-side */}
          <section>
            <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">Bill Blocks</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <BillBlock
                title="Supplier claim"
                tone="amber"
                bags={row.bill_raised_bags}
                rate={row.claimed_rate_per_bag_incl_gst}
                net={row.claimed_net_amount}
                cgst={row.claimed_cgst}
                sgst={row.claimed_sgst}
                gross={row.claimed_gross_amount}
                extra={{ 'Invoice No.': row.supplier_invoice_no, 'Invoice Date': row.supplier_invoice_date, 'E-way No.': row.eway_bill_no, 'Submitted': row.bill_submission_date }}
              />
              <BillBlock
                title="DM certified"
                tone="indigo"
                bags={row.certified_bags}
                rate={row.rate_per_bag_incl_gst}
                net={row.net_amount}
                cgst={row.cgst}
                sgst={row.sgst}
                gross={row.gross_amount}
              />
              <BillBlock
                title="Mgr Proc approved"
                tone="green"
                bags={row.mp_certified_bags}
                rate={row.mp_rate_per_bag_incl_gst}
                net={row.mp_net_amount}
                cgst={row.mp_cgst}
                sgst={row.mp_sgst}
                gross={row.mp_gross_amount}
              />
            </div>
          </section>

          {/* Audit trail — every actor's edit, chronologically */}
          <section>
            <h3 className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-1">
              <History className="w-3.5 h-3.5" /> Audit Trail
            </h3>
            {loadingAudit ? (
              <p className="text-xs text-gray-500">Loading history…</p>
            ) : audit.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No audit entries recorded.</p>
            ) : (
              <ol className="space-y-2 border-l-2 border-gray-200 ml-2 pl-4">
                {audit.map((e) => <AuditEntry key={e.id} e={e} />)}
              </ol>
            )}
          </section>

          {/* Payments */}
          <section>
            <h3 className="text-xs font-bold uppercase text-gray-500 mb-2">Payments (AO / AP)</h3>
            <div className="bg-gray-50 rounded border border-gray-200 p-3 text-sm mb-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Approved gross</span>
                <span className="tabular-nums font-medium">₹ {moneyFmt(row.mp_gross_amount ?? row.gross_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total paid</span>
                <span className="tabular-nums font-medium">₹ {moneyFmt(row.payment_released)}</span>
              </div>
              <div className="flex justify-between border-t mt-1 pt-1">
                <span className="text-gray-700 font-medium">Balance</span>
                <span className="tabular-nums font-bold text-rose-700">₹ {moneyFmt(row.balance_to_release)}</span>
              </div>
            </div>
            {loadingPayments ? (
              <p className="text-xs text-gray-500">Loading payments…</p>
            ) : payments.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No payments recorded yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-600">
                    <th className="text-left px-2 py-1">Date</th>
                    <th className="text-left px-2 py-1">Mode</th>
                    <th className="text-left px-2 py-1">UTR</th>
                    <th className="text-left px-2 py-1">Bank</th>
                    <th className="text-right px-2 py-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="px-2 py-1">{p.paid_on}</td>
                      <td className="px-2 py-1 uppercase">{p.mode}</td>
                      <td className="px-2 py-1">{p.utr_no || '—'}</td>
                      <td className="px-2 py-1">{p.bank_name || '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums">₹ {moneyFmt(p.amount_rs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
};

const ACTION_LABEL: Record<GunnyAuditEntry['action'], string> = {
  allot:            'Allotted bags to society',
  society_confirm:  'Society confirmed utilization',
  supplier_claim:   'Supplier filed bill claim',
  dm_certify:       'DM certified',
  mp_approve:       'Manager Procurement approved',
  reject:           'Rejected',
  reopen:           'Reopened (back to draft)',
  return_update:    'Return godown updated',
  payment_add:      'Payment recorded',
  payment_update:   'Payment updated',
  payment_remove:   'Payment removed',
};

const AuditEntry: React.FC<{ e: GunnyAuditEntry }> = ({ e }) => {
  const actorName = e.actor?.name || '—';
  const role = e.actor_role || e.actor?.role || '';
  const fieldCount =
    e.changed_fields && typeof e.changed_fields === 'object' ? Object.keys(e.changed_fields).length : 0;
  return (
    <li className="relative">
      <span className="absolute -left-[19px] top-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
      <div className="text-sm text-gray-900 font-medium">{ACTION_LABEL[e.action] || e.action}</div>
      <div className="text-xs text-gray-600">
        by <span className="font-medium">{actorName}</span>
        {role && <span className="text-gray-500"> ({role})</span>}
        <span className="ml-2 text-gray-400">{dateFmt(e.created_at)}</span>
      </div>
      {(e.from_status || e.to_status) && e.from_status !== e.to_status && (
        <div className="text-xs text-gray-500 mt-0.5">
          Status: <span className="font-mono">{e.from_status || '—'}</span> → <span className="font-mono">{e.to_status || '—'}</span>
        </div>
      )}
      {e.details && <div className="text-xs text-gray-600 mt-0.5 italic">{e.details}</div>}
      {fieldCount > 0 && (
        <details className="text-xs text-gray-600 mt-0.5">
          <summary className="cursor-pointer hover:text-gray-900">{fieldCount} field{fieldCount > 1 ? 's' : ''} changed</summary>
          <pre className="mt-1 p-2 bg-gray-50 border rounded overflow-x-auto text-[11px]">
{JSON.stringify(e.changed_fields, null, 2)}
          </pre>
        </details>
      )}
    </li>
  );
};

const ReturnGodownLabel: React.FC<{
  row: GunnyProformaRow;
  godowns: { id: number; godown_name: string }[];
}> = ({ row, godowns }) => {
  const effective = row.return_godown_id ?? row.godown_id;
  if (effective == null) {
    return <span className="text-gray-500 italic">Unassigned</span>;
  }
  const match = godowns.find((g) => g.id === effective);
  const name = match?.godown_name || `Godown ${effective}`;
  if (row.return_godown_id != null && row.return_godown_id !== row.godown_id) {
    return (
      <>
        <span className="font-medium">{name}</span>
        <span className="ml-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
          overridden
        </span>
      </>
    );
  }
  return (
    <>
      <span className="font-medium">{name}</span>
      <span className="ml-2 text-xs text-gray-500 italic">(default: source godown)</span>
    </>
  );
};

const TimelineItem: React.FC<{ label: string; at: string | null }> = ({ label, at }) => (
  <li className="flex items-center gap-2 text-sm">
    {at ? (
      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
    ) : (
      <CircleDashed className="w-4 h-4 text-gray-400 flex-shrink-0" />
    )}
    <span className={at ? 'text-gray-900' : 'text-gray-500'}>{label}</span>
    <span className="ml-auto text-xs text-gray-500">{dateFmt(at)}</span>
  </li>
);

const Grid: React.FC<{ cols?: number; children: React.ReactNode }> = ({ cols = 4, children }) => (
  <div className={`grid gap-3 text-sm ${cols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>{children}</div>
);

const Cell: React.FC<{ label: string; v: React.ReactNode }> = ({ label, v }) => (
  <div className="bg-gray-50 border rounded px-3 py-2">
    <p className="text-[10px] uppercase text-gray-500">{label}</p>
    <p className="font-medium text-gray-900 break-words">{v}</p>
  </div>
);

const BillBlock: React.FC<{
  title: string;
  tone: 'amber' | 'indigo' | 'green';
  bags: number | null | undefined;
  rate: number | null | undefined;
  net: number | null | undefined;
  cgst: number | null | undefined;
  sgst: number | null | undefined;
  gross: number | null | undefined;
  extra?: Record<string, string | null>;
}> = ({ title, tone, bags, rate, net, cgst, sgst, gross, extra }) => {
  const border = { amber: 'border-amber-200', indigo: 'border-indigo-200', green: 'border-emerald-200' }[tone];
  const head   = { amber: 'bg-amber-50 text-amber-800', indigo: 'bg-indigo-50 text-indigo-800', green: 'bg-emerald-50 text-emerald-800' }[tone];
  const fmt = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' :
    Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={`border rounded-lg overflow-hidden ${border}`}>
      <div className={`px-3 py-1.5 font-semibold text-xs uppercase ${head}`}>{title}</div>
      <div className="p-3 space-y-1 text-sm">
        <Kv k="Bags"  v={bags == null ? '—' : Number(bags).toLocaleString()} />
        <Kv k="Rate"  v={fmt(rate)} />
        {net != null && <Kv k="Net" v={fmt(net)} />}
        {cgst != null && <Kv k="CGST" v={fmt(cgst)} />}
        {sgst != null && <Kv k="SGST" v={fmt(sgst)} />}
        <Kv k="Gross" v={fmt(gross)} bold />
        {extra && Object.entries(extra).filter(([, v]) => v).map(([k, v]) => (
          <Kv key={k} k={k} v={v as string} />
        ))}
      </div>
    </div>
  );
};

const Kv: React.FC<{ k: string; v: React.ReactNode; bold?: boolean }> = ({ k, v, bold }) => (
  <div className="flex justify-between text-xs">
    <span className="text-gray-600">{k}</span>
    <span className={`tabular-nums ${bold ? 'font-bold text-gray-900' : 'text-gray-800'}`}>{v}</span>
  </div>
);

export default GunnyRowDetail;
